import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';

interface HtmlDocumentFrameProps {
  /** A complete HTML document fragment, e.g. a job ad with its own fonts and `<style>`. */
  html: string;
  /** Accessible name of the frame. */
  title: string;
  className?: string;
}

/**
 * Exhibitor-authored HTML shown exactly as authored, isolated from the app.
 *
 * Job ads are complete documents: their own fonts, `<style>` blocks and inline
 * styles, designed for a white page. Injected into the app they would restyle
 * the whole page and lose their own look to the dark theme, so they render in
 * a frame instead.
 *
 * The sandbox omits `allow-scripts`, so nothing in the ad runs. That keeps
 * `allow-same-origin` safe, and it is what lets the frame be measured from here
 * to fit its content rather than scroll inside the page.
 */
export function HtmlDocumentFrame({
  html,
  title,
  className,
}: HtmlDocumentFrameProps): ReactElement {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [height, setHeight] = useState<number>();

  // Rounded up from the exact height: `scrollHeight` rounds fractional heights
  // (common with percentage padding) down, and the frame then fell short by a
  // fraction of a pixel — enough for a scrollbar.
  const measure = useCallback((): void => {
    const root = frameRef.current?.contentDocument?.documentElement;

    if (root !== undefined) {
      setHeight(Math.ceil(root.getBoundingClientRect().height));
    }
  }, []);

  // Images and web fonts keep arriving after `load`, and each can change the
  // height; the observer follows them.
  const onLoad = useCallback((): void => {
    measure();
    observerRef.current?.disconnect();

    const body = frameRef.current?.contentDocument?.body;

    if (body !== undefined && body !== null && typeof ResizeObserver !== 'undefined') {
      observerRef.current = new ResizeObserver(measure);
      observerRef.current.observe(body);
    }
  }, [measure]);

  useEffect(
    () => (): void => {
      observerRef.current?.disconnect();
    },
    [],
  );

  return (
    <iframe
      ref={frameRef}
      title={title}
      srcDoc={frameDocument(html)}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      onLoad={onLoad}
      className={`block w-full border-0 bg-white ${className ?? ''}`}
      style={height === undefined ? undefined : { height }}
    />
  );
}

/**
 * Wraps the fragment in a page of its own. `<base target="_blank">` opens the
 * ad's links in a new tab rather than inside the frame; the image rule only
 * keeps oversized images from overflowing a phone screen.
 *
 * The page never scrolls: the frame is sized to it. Without `overflow: hidden`
 * a momentary scrollbar while the height catches up narrows the content, which
 * reflows percentage-sized ads to another height.
 */
export function frameDocument(html: string): string {
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<base target="_blank">
<style>html, body { margin: 0; background: #fff; } html { overflow: hidden; } img { max-width: 100%; height: auto; }</style>
</head>
<body>${html}</body>
</html>`;
}
